import { Dispatch, FunctionComponent, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { toast } from "react-toastify";

import { REANALYZE_IN_PROGRESS } from "@src/common/helpers/toastIds";
import { queryKeys } from "@src/config/queryKeys";
import useTestResults from "@src/modules/DeviceStatus/Devices/DeviceInfoView/ResultsView/hooks/useTestResults";
import { BoxHeadline } from "@src/modules/DeviceStatus/Devices/DeviceInfoView/shared/components";
import ConfirmationModal from "@src/modules/shared/components/Modal/ConfirmationModal";
import useModal from "@src/modules/shared/components/Modal/useModal";
import { ReactComponent as Star } from "@src/modules/shared/components/Results/icons/star.svg";
import useDeleteTestResult from "@src/modules/shared/components/Results/ResultsFormHeader/hooks/useDeleteTestResult";
import useSetBenchmarkStatus from "@src/modules/shared/components/Results/ResultsFormHeader/hooks/useSetAsBenchmark";
import {
  BenchmarkButton,
  ResultsButton,
  ResultsButtons,
  ResultsHeaderWrapper,
  ResultsStatusWrapper,
} from "@src/modules/shared/components/Results/ResultsFormHeader/ResultsFormHeader.components";
import TestsStatus from "@src/modules/shared/components/TestsStatus";
import Tooltip from "@src/modules/shared/components/Tooltip";
import { ReactComponent as Bin } from "@src/modules/shared/icons/bin.svg";
import { ReactComponent as Edit } from "@src/modules/shared/icons/edit.svg";
import { StudyTestResult, TestStatus } from "@src/modules/shared/types";
import useReanalyzeSingleTest from "@src/modules/shared/components/Results/ResultsFormHeader/hooks/useReanalyzeSingleTest";
import KebabMenu from "@src/modules/shared/components/KebabMenu";
import { KebabOption } from "@src/modules/shared/components/KebabMenu/KebabMenu.components";
import { ReactComponent as Refresh } from "@src/modules/shared/icons/refresh.svg";

interface Props {
  selectedStudyId: string;
  selectedTest: StudyTestResult;
  isEditingAvailable: boolean;
  isEdit: boolean;
  setIsEdit: Dispatch<SetStateAction<boolean>>;
  onSave: () => void;
  onCancel: () => void;
}

const ResultsFormHeader: FunctionComponent<Props> = ({
  selectedStudyId,
  selectedTest,
  isEditingAvailable,
  isEdit,
  setIsEdit,
  onSave,
  onCancel,
}) => {
  const { t } = useTranslation();
  const { openFixedModal, closeModal } = useModal();
  const queryClient = useQueryClient();
  const { CALENDAR_STUDIES, DEVICE_INFO, DAY_TEST_RESULTS } = queryKeys;

  const { data: testResultsData } = useTestResults(
    selectedStudyId,
    selectedTest.id,
  );

  const reanalyzeMutation = useReanalyzeSingleTest();

  const hasResults = testResultsData && testResultsData?.results.length > 0;

  const deleteMutation = useDeleteTestResult(selectedStudyId, selectedTest.id);

  const setBenchmarkStatusMutation = useSetBenchmarkStatus(
    selectedStudyId,
    selectedTest.id,
  );

  const setBenchmarkStatus = () =>
    setBenchmarkStatusMutation.mutate(!testResultsData?.isBaseTest, {
      onSuccess: () => {
        queryClient.invalidateQueries();
      },
    });

  const onConfirmDelete = async () => {
    deleteMutation.mutate(selectedTest!.id, {
      onSuccess: () => {
        queryClient.invalidateQueries(DEVICE_INFO);
        queryClient.invalidateQueries(CALENDAR_STUDIES);
        queryClient.invalidateQueries(DAY_TEST_RESULTS);
      },
    });
  };

  const deleteTest = () => {
    openFixedModal(
      <ConfirmationModal
        header={t("Are you sure?")}
        isDeleteConfirmation
        confirmButtonLabel={t("Delete")}
        cancelButtonLabel={t("Cancel")}
        onConfirm={onConfirmDelete}
      />,
    );
  };

  const handleReanalyzeConfirm = () =>
    reanalyzeMutation.mutate(
      { studyId: selectedStudyId, testId: selectedTest.testId },
      {
        onSuccess: () => {
          queryClient.invalidateQueries();
          toast.success(t("Reanalyze in progress"), {
            toastId: REANALYZE_IN_PROGRESS,
          });
        },
      },
    );

  const handleReanalyze = () =>
    openFixedModal(
      <ConfirmationModal
        header={t("Are you sure?")}
        description={t(
          "You are about to reanalyze selected test This can take a while",
        )}
        cancelButtonLabel={t("Cancel")}
        onCancel={closeModal}
        confirmButtonLabel={t("Reanalyze")}
        onConfirm={handleReanalyzeConfirm}
      />,
    );

  const editTest = () => setIsEdit(true);
  const cancelEdit = () => {
    setIsEdit(false);
    onCancel();
  };

  const statusLabel =
    testResultsData?.resultStatus === TestStatus.Ready
      ? t("Test passed")
      : testResultsData?.resultStatus === TestStatus.InProgress
      ? t("Test in progress")
      : testResultsData?.resultStatus === TestStatus.ActionNeeded
      ? t("Update results")
      : t("Test failed");

  if (!testResultsData) return null;

  return (
    <ResultsHeaderWrapper>
      <ResultsStatusWrapper>
        <BoxHeadline>{t("Results")}</BoxHeadline>
        {testResultsData?.resultStatus !== TestStatus.Notes && (
          <TestsStatus
            status={testResultsData.resultStatus}
            customLabel={statusLabel}
          />
        )}
      </ResultsStatusWrapper>
      {isEdit ? (
        <ResultsButtons>
          <ResultsButton type="button" onClick={cancelEdit}>
            {t("Cancel")}
          </ResultsButton>
          <ResultsButton type="button" isSaveButton={true} onClick={onSave}>
            {t("Save")}
          </ResultsButton>
        </ResultsButtons>
      ) : (
        <ResultsButtons>
          {testResultsData.isSupportingRelativeTests && (
            <Tooltip
              description={t("Clear Benchmark")}
              isActive={testResultsData?.isBaseTest}
            >
              <BenchmarkButton
                type="button"
                isBaseTest={testResultsData?.isBaseTest}
                isSaveButton={testResultsData?.isBaseTest}
                onClick={setBenchmarkStatus}
                disabled={!isEditingAvailable}
              >
                <Star />
                {testResultsData.isBaseTest
                  ? t("Benchmark")
                  : t("Set as Benchmark")}
              </BenchmarkButton>
            </Tooltip>
          )}
          {hasResults && !selectedTest.isTestDeleted && (
            <ResultsButton
              type="button"
              onClick={editTest}
              disabled={!isEditingAvailable}
            >
              <Edit />
              {t("Edit")}
            </ResultsButton>
          )}
          <KebabMenu hasRelativePosition isPositionDown>
            <KebabOption onClick={deleteTest} disabled={!isEditingAvailable}>
              <Bin />
              {t("Delete Test")}
            </KebabOption>
            <KebabOption
              onClick={handleReanalyze}
              disabled={!isEditingAvailable}
            >
              <Refresh />
              {t("Reanalyze Test")}
            </KebabOption>
          </KebabMenu>
        </ResultsButtons>
      )}
    </ResultsHeaderWrapper>
  );
};

export default ResultsFormHeader;
